'use client';

import { useTranslations } from 'next-intl';
// eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration than use()
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { ConfirmationModal } from '@/components/Modal';
import { useBoolean } from '@/hooks';

export type ConfirmationOptions = {
  title?: string;
  description?: string;
  confirmText?: string;
  rejectText?: string;
  isCritical?: boolean;
};

export type ConfirmationContextValue = {
  confirm: (options?: ConfirmationOptions) => Promise<boolean>;
};

const ConfirmationContext = createContext<ConfirmationContextValue | null>(null);

/**
 * Hook to access the confirmation context
 * @returns Confirmation context value
 * @throws Error if used outside of ConfirmationProvider
 */
export function useConfirmation(): ConfirmationContextValue {
  // eslint-disable-next-line react/no-use-context -- useContext is more stable for SSR/hydration
  const context = useContext(ConfirmationContext);
  if (context === null) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context;
}

type ConfirmationProviderProps = {
  children: React.ReactNode;
};

type ConfirmationState = {
  resolve: (value: boolean) => void;
} & ConfirmationOptions;

/**
 * Provider component for confirmation dialogs
 * @param props - Component props
 * @param props.children - React children
 */
export function ConfirmationProvider({ children }: ConfirmationProviderProps) {
  const translate = useTranslations();
  const [confirmationState, setConfirmationState] = useState<ConfirmationState | null>(null);
  const isOpen = useBoolean();

  const confirm = useCallback((options: ConfirmationOptions = {}): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setConfirmationState({
        resolve,
        title: options.title !== undefined ? options.title : translate('modal.confirmation.title'),
        description: options.description !== undefined ? options.description : translate('navigationModal.unsavedChangesMessage'),
        confirmText: options.confirmText !== undefined ? options.confirmText : translate('modal.confirm'),
        rejectText: options.rejectText !== undefined ? options.rejectText : translate('modal.cancel'),
        isCritical: options.isCritical === true,
      });
      isOpen.onTrue();
    });
  }, [translate, isOpen]);

  const handleConfirm = useCallback(() => {
    if (confirmationState !== null) {
      confirmationState.resolve(true);
      setConfirmationState(null);
      isOpen.onFalse();
    }
  }, [confirmationState, isOpen]);

  const handleCancel = useCallback(() => {
    if (confirmationState !== null) {
      confirmationState.resolve(false);
      setConfirmationState(null);
      isOpen.onFalse();
    }
  }, [confirmationState, isOpen]);

  const handleOpenChange = useCallback((open: boolean) => {
    if (open === false && confirmationState !== null) {
      confirmationState.resolve(false);
      setConfirmationState(null);
      isOpen.onFalse();
    }
  }, [confirmationState, isOpen]);

  const contextValue = useMemo(() => ({ confirm }), [confirm]);

  return (
    <ConfirmationContext value={contextValue}>
      {children}
      {confirmationState !== null && (
        <ConfirmationModal
          open={isOpen.value}
          onOpenChange={handleOpenChange}
          title={confirmationState.title}
          description={confirmationState.description}
          confirmText={confirmationState.confirmText}
          rejectText={confirmationState.rejectText}
          onConfirm={handleConfirm}
          onReject={handleCancel}
          isCritical={confirmationState.isCritical}
        />
      )}
    </ConfirmationContext>
  );
}

// Make useConfirmation the default export
export default useConfirmation;
